type Float32List = Float32Array | readonly GLfloat[]

function floatSetter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: GLfloat) {
		gl.uniform1f(location, v)
	}
}

function floatArraySetter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Float32List) {
		gl.uniform1fv(location, v)
	}
}

function floatVec2Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Float32List) {
		gl.uniform2fv(location, v)
	}
}

function floatVec3Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Float32List) {
		gl.uniform3fv(location, v)
	}
}

function floatVec4Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Float32List) {
		gl.uniform4fv(location, v)
	}
}

function intSetter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: GLint) {
		gl.uniform1i(location, v)
	}
}

function boolSetter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: boolean) {
		gl.uniform1i(location, v ? 1 : 0)
	}
}

function intArraySetter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Int32List) {
		gl.uniform1iv(location, v)
	}
}

function intVec2Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Int32List) {
		gl.uniform2iv(location, v)
	}
}

function intVec3Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Int32List) {
		gl.uniform3iv(location, v)
	}
}

function intVec4Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Int32List) {
		gl.uniform4iv(location, v)
	}
}

function uintSetter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: GLuint) {
		gl.uniform1ui(location, v)
	}
}

function uintArraySetter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Uint32List) {
		gl.uniform1uiv(location, v)
	}
}

function uintVec2Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Uint32List) {
		gl.uniform2uiv(location, v)
	}
}

function uintVec3Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Uint32List) {
		gl.uniform3uiv(location, v)
	}
}

function uintVec4Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Uint32List) {
		gl.uniform4uiv(location, v)
	}
}

function floatMat2Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Float32List) {
		gl.uniformMatrix2fv(location, false, v)
	}
}

function floatMat3Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Float32List) {
		gl.uniformMatrix3fv(location, false, v)
	}
}

function floatMat4Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Float32List) {
		gl.uniformMatrix4fv(location, false, v)
	}
}

function floatMat23Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Float32List) {
		gl.uniformMatrix2x3fv(location, false, v)
	}
}

function floatMat32Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Float32List) {
		gl.uniformMatrix3x2fv(location, false, v)
	}
}

function floatMat24Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Float32List) {
		gl.uniformMatrix2x4fv(location, false, v)
	}
}

function floatMat42Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Float32List) {
		gl.uniformMatrix4x2fv(location, false, v)
	}
}

function floatMat34Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Float32List) {
		gl.uniformMatrix3x4fv(location, false, v)
	}
}

function floatMat43Setter(gl: WebGL2RenderingContext, location: WebGLUniformLocation) {
	return function (v: Float32List) {
		gl.uniformMatrix4x3fv(location, false, v)
	}
}

export const setterCreators = {
	// basic types
	float: floatSetter,
	int: intSetter,
	uint: uintSetter,
	bool: boolSetter,

	// arrays
	floatArray: floatArraySetter,
	intArray: intArraySetter,
	uintArray: uintArraySetter,

	// float vectors
	vec2: floatVec2Setter,
	vec3: floatVec3Setter,
	vec4: floatVec4Setter,

	// signed integer vectors
	ivec2: intVec2Setter,
	ivec3: intVec3Setter,
	ivec4: intVec4Setter,

	// unsigned integer vectors
	uvec2: uintVec2Setter,
	uvec3: uintVec3Setter,
	uvec4: uintVec4Setter,

	// boolean vectors
	bvec2: intVec2Setter,
	bvec3: intVec3Setter,
	bvec4: intVec4Setter,

	// float matrices
	mat2: floatMat2Setter,
	mat3: floatMat3Setter,
	mat4: floatMat4Setter,
	mat2x3: floatMat23Setter,
	mat2x4: floatMat24Setter,
	mat3x2: floatMat32Setter,
	mat3x4: floatMat34Setter,
	mat4x2: floatMat42Setter,
	mat4x3: floatMat43Setter,

	// samplers
	sampler2D: intSetter,
	sampler2DArray: intSetter,
	sampler3D: intSetter,
}

export function createUniformSetters(
	gl: WebGL2RenderingContext,
	program: WebGLProgram,
	uniforms: UniformTypes,
) {
	const uniformSetters: UniformSetters<UniformTypes> = {}

	for (const name in uniforms) {
		const location = gl.getUniformLocation(program, name)
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const createSetter = setterCreators[uniforms[name]!]

		if (location == null) {
			console.warn(`Uniform location could not be found: ${name}`)
			// setter becomes a no-op if uniform location is null
			uniformSetters[name] = Function.prototype as any
			continue
		}

		uniformSetters[name] = createSetter(gl, location)
	}

	return uniformSetters
}

export type SetterCreators = typeof setterCreators
export type UniformTypes = Record<string, keyof SetterCreators>
export type UniformSetters<U extends UniformTypes> = Record<
	keyof U,
	ReturnType<SetterCreators[U[keyof U]]>
>
export type UniformValue<U extends UniformTypes, K extends keyof U> = Parameters<
	UniformSetters<U>[K]
>[0]
